// Native UI for the External Clients Nest — CTSK-166 Phase B.
//
// Route: /mission-control/nests/external-clients
// Registered in NATIVE_NEST_TYPES + router.tsx.
//
// Renders documents in the "External Clients" container grouped by
// `access/*` state via a tab strip (Pending / Approved / Denied /
// Revoked / Introductions / All). Each row shows title + kind chip +
// state chip + created-at timestamp. Click-through opens the underlying
// document detail where CPs, activity, and the Approve action live.
//
// CTSK-300 (2026-08-08): Introductions tab — `access/introduction`
// docs (created by tasko_greet, CTSK-210) get their own tab with an
// Introduction-shaped row (Client / Introduced / Kind — no access-
// request State/Actions, those concepts don't apply). The "All" tab
// keeps the pre-existing mixed behavior with `—` fallbacks. Tab strip
// converted to the shared .tabs/.tab classes scoped under
// `tabs--external-clients` (mirrors CTSK-298's `tabs--settings`
// pattern), and the table gained the `table--external-clients` density
// treatment (2-line Requested column, tightened Kind/State widths).
//
// Phase B MVP scope:
//   - Read-only list view with filter tabs
//   - Client-side tag filtering (list query returns all docs in the
//     container, we bucket by tag)
//   - No approve button yet — Phase B2 adds an inline "Approve"
//     button that calls /documents/:id/actions/approve-and-mint-pat
//     and shows the one-time token in a modal
//
// Depends on:
//   - Container slug 'tool-external-clients' being present (installed
//     via seed/bundles/external-clients-nest.bundle.json)
//   - documents:view capability on the caller

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { DocumentListItem, AccessRequestSummary, PatLifecycleSummary } from '@captain-tasko/shared';
import { apiList, apiSingle } from '../lib/api.js';
import { SkeletonRows } from '../components/Skeleton.js';
import { toast } from '../components/Toast.js';
import { copyToClipboard } from '../lib/clipboard.js';
import { HelpLink } from '../components/HelpLink.js';
import { StateBadge } from '../components/StateBadge.js';
import {
  envFilenameFor,
  buildSessionEnv,
  triggerDownload,
  pickApiBaseUrl,
} from '../lib/env-file.js';
import { KeyRound, Bot, Webhook, Cpu, Wrench, Copy, Check, Download, Fingerprint, ExternalLink } from 'lucide-react';
import { RequestNoteChip } from './external-clients/RequestNoteChip.js';
import { REQUEST_NOTE_PROMPT } from './external-clients/request-note-text.js';
import {
  RowTable,
  useUrlTableSort,
  rowActionButtonStyle,
  type RowTableColumn,
} from '../components/shared-table/RowTable.js';
import {
  ToolbarRow,
  ChipFilterButton,
  readUrlParam,
  writeUrlParam,
  type ToolbarOption,
} from '../components/shared-table/ToolbarRow.js';
import type { Accessors } from '../lib/sort.js';

// ── Nest constants ──────────────────────────────────────────────────
// The container name mirrors seed/bundles/external-clients-nest.bundle.json.
const CONTAINER_NAME = 'External Clients';

const ACCESS_STATES = ['pending', 'approved', 'denied', 'revoked'] as const;
type AccessState = (typeof ACCESS_STATES)[number] | 'all';

// CTSK-300 — the Introductions tab sits alongside the access-state
// tabs but is NOT an access state: introduction docs (tasko_greet,
// CTSK-210) never transition through the request workflow. TabKey is
// the strip's superset.
type TabKey = AccessState | 'introductions';
const INTRODUCTION_TAG = 'access/introduction';

// CTSK-357 — the full strip, in render order (CTSK-300's tab list is
// the contract). Also validates the router-passed `?tab=` deep-link
// param: the sidebar/Mission Control ECN nest tile links here with
// `?tab=all`, and any tab is now URL-addressable.
const TAB_KEYS: readonly TabKey[] = [...ACCESS_STATES, 'introductions', 'all'];

/** Coerce a raw `?tab=` value to a TabKey, or null when absent/unknown
 *  (unknown/stale deep-links fall back to the page default — same
 *  junk-tolerance stance as Settings' coerceSection, CTSK-346). */
function coerceTab(raw: string | null | undefined): TabKey | null {
  return raw != null && (TAB_KEYS as readonly string[]).includes(raw) ? (raw as TabKey) : null;
}

/** Introduction docs are titled `Introduction — <friendlyName>` by the
 *  greet route (apps/api/src/routes/introductions.ts). The admin
 *  endpoint's trimmed CP whitelist doesn't carry intro_friendly_name,
 *  so the Client column derives the friendly name (== the session's
 *  sourceTag) from that deterministic title format. */
function introFriendlyName(title: string): string {
  return title.replace(/^Introduction\s+—\s+/, '');
}

// ── CTSK-476 — Approved-tab credential filter ───────────────────────
// The Approved tab is the operator's "who currently has access" view,
// but the doc's `access/approved` tag only records that a request WAS
// approved — out-of-band revokes (DELETE /pats/:id, the CTSK-414
// rotation sweep, manual SQL) kill the PAT without touching the doc.
// The server now cross-references personal_access_tokens per row
// (`patLifecycle`); this segmented control decides whether rows whose
// credential is revoked/expired are shown (greyed + badged) or hidden.
// Default "active" — the tab answers its own question by default.
// Persisted per-browser (not URL-mirrored like the All-tab toolbar:
// it's a viewing preference, not a shareable query).
type ApprovedFilter = 'active' | 'all';
const APPROVED_FILTER_STORAGE_KEY = 'ctsk-476/approved-filter';
const APPROVED_FILTER_OPTIONS: readonly { value: ApprovedFilter; label: string }[] = [
  { value: 'active', label: 'Active credentials only' },
  { value: 'all', label: 'All-time-approved' },
];

function readApprovedFilter(): ApprovedFilter {
  try {
    const v = window.localStorage.getItem(APPROVED_FILTER_STORAGE_KEY);
    return v === 'all' ? 'all' : 'active';
  } catch {
    return 'active';
  }
}

function writeApprovedFilter(v: ApprovedFilter): void {
  try {
    window.localStorage.setItem(APPROVED_FILTER_STORAGE_KEY, v);
  } catch {
    /* storage unavailable (private mode, quota) — selection stays in-memory */
  }
}

/** True when the row's credential is no longer usable. `null`
 *  lifecycle (pre-0.1.249 server that doesn't emit the field) reads as
 *  usable — we don't grey rows on missing information. */
function isDeadCredential(lifecycle: PatLifecycleSummary | null): boolean {
  return lifecycle !== null && (lifecycle.state === 'revoked' || lifecycle.state === 'expired');
}

/** YYYY-MM-DD (UTC) for the badge suffix; null on unparseable input. */
function isoDate(iso: string | null): string | null {
  if (!iso) return null;
  const t = new Date(iso);
  return Number.isNaN(t.getTime()) ? null : t.toISOString().slice(0, 10);
}

/** Kind label for the Introductions tab, driven by the whitelisted
 *  `client_kind` CP (the greet route stamps 'cowork-session'). */
const INTRO_KIND_LABELS: Record<string, string> = {
  'cowork-session': 'Cowork session',
  'claude-mcp-session': 'Claude MCP',
};

const CLIENT_KINDS = [
  { tag: 'client/claude-mcp-session', label: 'Claude MCP', icon: Bot },
  { tag: 'client/webhook-consumer',   label: 'Webhook',     icon: Webhook },
  { tag: 'client/ci-bot',             label: 'CI Bot',      icon: Cpu },
  { tag: 'client/external-mcp',       label: 'External MCP', icon: Cpu },
  { tag: 'client/human-tool',         label: 'Human Tool',  icon: Wrench },
] as const;

const ACCESS_COLORS: Record<Exclude<AccessState, 'all'>, string> = {
  pending:  '#F59E0B',
  approved: '#22C55E',
  denied:   '#EF4444',
  revoked:  '#6B7280',
};

// ── Types ────────────────────────────────────────────────────────────

/** A row in the list — extracted subset of AccessRequestSummary for the table.
 *  CTSK-193 (2026-07-22): now sourced from the /nests/:slug/access-requests
 *  endpoint, so `_by_display_name` fields are server-enriched from the
 *  users table (CTSK-186). Falls back to `_by_user_id` ULID prefix when
 *  the enrichment is null (e.g. deleted user). */
interface ClientRow {
  id: string;
  title: string;
  createdAt: string;
  /** CTSK-466 — age proxy for the archive filter. The trimmed
   *  AccessRequestSummary CP whitelist carries no denied_at/revoked_at
   *  timestamps (only reasons + actors), but the deny/revoke tag swap
   *  touches the doc, so updatedAt approximates the state-change time.
   *  Judgment call flagged in the CTSK-466 ship comment. */
  updatedAt: string;
  tags: string[];
  accessState: Exclude<AccessState, 'all'> | 'unknown';
  clientKind: (typeof CLIENT_KINDS)[number] | null;
  approvedAt: string | null;
  approvedByUserId: string | null;
  approvedByDisplayName: string | null;
  deniedAt: string | null;
  deniedByUserId: string | null;
  deniedByDisplayName: string | null;
  denialReason: string | null;
  revokedAt: string | null;
  revokedByUserId: string | null;
  revokedByDisplayName: string | null;
  revocationReason: string | null;
  // CTSK-296: requester TTL preference / effective TTL / pre-clamp value.
  requestedExpiresInDays: number | null;
  effectiveExpiresInDays: number | null;
  expiryClampedFrom: number | null;
  // CTSK-300: introduction discriminator + raw client_kind CP (drives
  // the Introductions tab's Kind chip without touching CLIENT_KINDS,
  // which is tag-derived and shared with the access-request tabs).
  isIntroduction: boolean;
  clientKindRaw: string | null;
  /** CTSK-476: server cross-reference of the linked PAT's present state.
   *  null when the server didn't emit it (pre-0.1.249). */
  patLifecycle: PatLifecycleSummary | null;
}

// ── Helpers ─────────────────────────────────────────────────────────

function deriveAccessState(tags: string[]): ClientRow['accessState'] {
  if (tags.includes('access/pending'))  return 'pending';
  if (tags.includes('access/approved')) return 'approved';
  if (tags.includes('access/denied'))   return 'denied';
  if (tags.includes('access/revoked'))  return 'revoked';
  return 'unknown';
}

function deriveClientKind(tags: string[]): ClientRow['clientKind'] {
  return CLIENT_KINDS.find((k) => tags.includes(k.tag)) ?? null;
}

/** CTSK-296 follow-up: render a TTL that may be fractional days.
 *  Whole days → "14d"; sub-day / fractional (hours-override mints) →
 *  rounded hours, e.g. 0.5 → "12h", 1.5 → "36h". */
function formatTtl(days: number): string {
  if (Number.isInteger(days)) return `${days}d`;
  return `${Math.round(days * 24)}h`;
}

function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diff = now - then;
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

// ── CTSK-466 — sort + toolbar filters + archive ─────────────────────

/** Column config for the shared RowTable shell (Action unsortable).
 *  headerClassName keeps the pre-existing col--kind/col--state width
 *  caps working under the ported .grid treatment. */
const ECN_COLUMNS: readonly RowTableColumn[] = [
  { key: 'client', label: 'Client' },
  { key: 'kind', label: 'Kind', headerClassName: 'col--kind' },
  { key: 'state', label: 'State', headerClassName: 'col--state' },
  { key: 'requested', label: 'Requested' },
  { key: 'action', label: 'Action', sortable: false, align: 'right' },
];

/** Sort accessors — lib/sort.ts normalize sorts nulls last and ISO
 *  strings lexicographically (== chronologically). */
const ECN_SORT_ACCESSORS: Accessors<ClientRow> = {
  client: (r) => r.title,
  kind: (r) => r.clientKind?.label ?? null,
  state: (r) => (r.accessState === 'unknown' ? null : r.accessState),
  requested: (r) => r.createdAt,
};

/** Kind dropdown mirrors what the Kind column can render. */
const ECN_KIND_OPTIONS: readonly ToolbarOption[] = CLIENT_KINDS.map((k) => ({
  value: k.tag,
  label: k.label,
}));

const ECN_STATE_OPTIONS: readonly ToolbarOption[] = ACCESS_STATES.map((s) => ({
  value: s,
  label: s.charAt(0).toUpperCase() + s.slice(1),
}));

const ARCHIVE_AGE_MS = 30 * 24 * 60 * 60 * 1000;

/** Archived = denied/revoked more than 30 days ago. Age proxy is
 *  updatedAt (see the ClientRow field comment — the trimmed shape has
 *  no denied_at/revoked_at). Introductions and pending/approved rows
 *  are never archived. */
function isArchivedClientRow(r: ClientRow, now = Date.now()): boolean {
  if (r.accessState !== 'denied' && r.accessState !== 'revoked') return false;
  const t = new Date(r.updatedAt).getTime();
  return !Number.isNaN(t) && now - t > ARCHIVE_AGE_MS;
}

// ── Approve endpoint envelope ────────────────────────────────────────
// Mirrors apps/api/src/routes/documents.ts /actions/approve-and-mint-pat
// response shape. The token is the one-time raw PAT; MUST be surfaced
// exactly once and never persisted or logged client-side.

interface ApproveResult {
  documentId: string;
  workflowName: string;
  mintedPat: {
    id: string;
    token: string;
    prefix: string;
    name: string;
    grantedCapabilities: string[];
    droppedCapabilities: string[];
    // CTSK-176 (0.1.56+): needed to build the per-session .env file the
    // client saves at ~/.config/tasko-mcp/<snake_case_source>.env. Both
    // fields are optional in the interface because change-client-role's
    // mintedPat currently omits them (fast-follow ticket to extend the
    // change-role adapter mirror-symmetric). Download button falls back
    // gracefully when either is absent.
    sourceTag?: string;
    subUuidTailFirst8?: string | null;
    // CTSK-187 (2026-07-22): server-emitted authoritative API base URL
    // for the downloaded .env's TASKO_BASE_URL. Composed server-side
    // from API_PUBLIC_BASE_URL env var (prod) or req.protocol+Host
    // header (dev). Falls back to window.location.origin on the UI side
    // if server doesn't emit — preserves behavior during rolling
    // deploys. Once ubiquitous, remove the fallback.
    apiBaseUrl?: string | null;
    // CTSK-190 (2026-07-22): captain-tasko API version at mint time.
    // Stamped into downloaded .env as TASKO_API_VERSION_AT_MINT +
    // header comment for post-hoc diagnostics. Optional because pre-
    // CTSK-190 servers don't emit; UI falls back to "unknown" in that
    // case. Once every deployed API includes it, drop the fallback.
    apiVersionAtMint?: string | null;
    // CTSK-296 (0.1.114+): TTL the mint actually used + the pre-clamp
    // requested value when the requester's preference exceeded
    // mintPolicy.maxExpiresInDays (null when no clamp fired). Optional
    // because pre-CTSK-296 servers don't emit.
    effectiveExpiresInDays?: number | null;
    expiryClampedFrom?: number | null;
    // CTSK-311 (0.1.125): role handle the request was filed with (doc CP
    // role_handle) + the mint org. Drives the role-aware .env emission
    // (extra TASKO_* keys for tootasko-ext — see lib/env-file.ts
    // buildRoleEnvLines). Optional because pre-0.1.125 servers don't
    // emit; the Download button degrades to the base emission.
    roleHandle?: string | null;
    activeOrgId?: string | null;
  };
}

// ── Change-role endpoint envelope (0.1.5X+, CTSK-170 Part A/4) ──────
// Mirrors packages/api-client ChangeClientRoleResult. Same mintedPat
// sub-shape as ApproveResult so the reveal panel can render either
// without branching on which action produced it.
interface ChangeRoleResult {
  documentId: string;
  workflowName: string;
  mode: 'elevate' | 'downgrade' | 'sidegrade' | 'noop';
  oldPatId: string;
  oldRoleHandle: string | null;
  newRoleHandle: string;
  newRoleVersion: string;
  mintedPat: ApproveResult['mintedPat'];
}

// Role catalog envelope — matches GET /role-catalog (0.1.5X+, Part A/2).
interface RoleCatalogEntry {
  handle: string;
  description: string;
  capabilities: string[];
  version: string;
  category: string;
  deprecated?: boolean;
  replacedBy?: string;
}

// ── CTSK-176 helpers — .env download for the approve-and-mint reveal ──

// envFilenameFor, buildSessionEnv, triggerDownload, pickApiBaseUrl were
// inlined here originally (CTSK-176 approve-and-mint reveal + CTSK-187
// apiBaseUrl chain + CTSK-211 loopback gating). Extracted to
// `apps/web/src/lib/env-file.ts` on 2026-07-29 to dedupe against the
// Settings-mint PatsPanel ship. All four helpers now live in that lib
// and are imported at the top of this file. No file today imports any of
// them from THIS file; if that changes, they should import from
// `apps/web/src/lib/env-file` instead.

// ── Component ───────────────────────────────────────────────────────

/** Onboarding card — "Finding the session UID & tail" strip (CTSK-TBD).
 *  Exported so the page test can assert slug ↔ label pairs. */
export const UID_DISCOVERY_LINKS: readonly { label: string; path: string }[] = [
  { label: 'tasko-mcp setup', path: '/help/tasko-mcp-setup' },
  { label: 'Session identity & subUuid', path: '/help/session-identity-hardening' },
  { label: 'Project sessions guide', path: '/help/project-sessions' },
  { label: 'Role catalog', path: '/help/role-catalog' },
];

/** Onboarding card — API reference row (CTSK-TBD). */
export const API_REFERENCE_LINKS: readonly { label: string; path: string }[] = [
  { label: 'PATs API', path: '/help/pats-api' },
  { label: 'Ingest API contract', path: '/help/documents-ingest-api' },
  { label: 'API contract reference', path: '/help/api-contract' },
];

export function ExternalClientsNest({
  onNavigate,
  tabParam,
}: {
  onNavigate: (p: string) => void;
  /** CTSK-357 — raw `?tab=` from the router (nestParam precedent,
   *  ctsk-056). Validated via coerceTab; null/unknown → 'pending'. */
  tabParam?: string | null;
}) {
  const [activeState, setActiveState] = useState<TabKey>(() => coerceTab(tabParam) ?? 'pending');
  // CTSK-357 — re-sync when the query string changes without a remount
  // (pushState-only navigation keeps this page mounted; the lazy
  // initializer above only runs once). Absent/unknown param leaves the
  // current selection alone rather than yanking the strip back.
  useEffect(() => {
    const coerced = coerceTab(tabParam);
    if (coerced) setActiveState(coerced);
  }, [tabParam]);

  // CTSK-466 — All-tab toolbar filters + archive toggle, URL-mirrored
  // (?q=&kind=&state=&archived=1). The page's own ?tab= param (CTSK-357)
  // is preserved — we only write our own keys, and tab clicks go
  // through onNavigate/pushState which keeps foreign params intact
  // only for the params it carries; the toolbar params deliberately
  // reset on tab navigation (the toolbar exists only on All anyway).
  const [search, setSearchRaw] = useState(() => readUrlParam('q'));
  const [kindFilter, setKindFilterRaw] = useState(() => readUrlParam('kind'));
  const [stateFilter, setStateFilterRaw] = useState(() => readUrlParam('state'));
  const [showArchived, setShowArchivedRaw] = useState(() => readUrlParam('archived') === '1');
  const setSearch = (v: string) => {
    setSearchRaw(v);
    writeUrlParam('q', v.trim() ? v : null);
  };
  const setKindFilter = (v: string) => {
    setKindFilterRaw(v);
    writeUrlParam('kind', v || null);
  };
  const setStateFilter = (v: string) => {
    setStateFilterRaw(v);
    writeUrlParam('state', v || null);
  };
  const setShowArchived = (v: boolean) => {
    setShowArchivedRaw(v);
    writeUrlParam('archived', v ? '1' : null);
  };
  // CTSK-476 — Approved-tab credential filter (localStorage-backed).
  const [approvedFilter, setApprovedFilterRaw] = useState<ApprovedFilter>(() => readApprovedFilter());
  const setApprovedFilter = (v: ApprovedFilter) => {
    setApprovedFilterRaw(v);
    writeApprovedFilter(v);
  };
  // CTSK-197 (2026-07-23): `origin` discriminator on the reveal state.
  // Both approve-mint and change-role hydrate the same `mintedPat`
  // sub-shape, but the header messaging + secondary line differ. Origin
  // lets the reveal panel branch its copy without duck-typing on
  // ChangeRoleResult-specific fields.
  const [revealed, setRevealed] = useState<{
    doc: ClientRow;
    result: ApproveResult;
    origin: 'approve' | 'role-change';
    /** For role-change reveals: the new role handle + mode (elevate/downgrade/sidegrade).
     *  Undefined when origin === 'approve'. */
    roleChange?: { newRoleHandle: string; mode: string };
  } | null>(null);
  // CTSK-211: the editable API base URL for the downloaded .env's
  // TASKO_BASE_URL. Seeded from pickApiBaseUrl() whenever a new mint
  // reveal opens; the operator can overwrite it before Download (slot 4
  // of the ratified chain — manual override for topologies where every
  // automatic slot guesses wrong).
  const [apiBaseUrlField, setApiBaseUrlField] = useState('');
  useEffect(() => {
    if (revealed) {
      setApiBaseUrlField(pickApiBaseUrl(revealed.result.mintedPat.apiBaseUrl));
    }
  }, [revealed]);
  const qc = useQueryClient();

  // CTSK-296 follow-up (0.1.116): mintPolicy summary for the approve
  // dialog — role default, requester clamp ceiling, and the override
  // bounds the route enforces. Lazy + cached; a 404 (no approve
  // workflow installed) just leaves the dialog copy generic.
  const mintPolicyQ = useQuery({
    queryKey: ['external-clients-mint-policy'],
    queryFn: () =>
      apiSingle<{
        expiresInDays: number | null;
        maxExpiresInDays: number;
        overrideMinDays: number;
        overrideMaxDays: number;
      }>('/nests/external-clients/mint-policy'),
    retry: false,
  });

  const approveMut = useMutation({
    // CTSK-296: optional operator override rides in the POST body —
    // days (int 1..364) or hours (int 1..8736), never both. Undefined →
    // empty body → server resolves requested-vs-maxExpiresInDays (or
    // role default) itself.
    mutationFn: (input: { row: ClientRow; expiresInDays?: number; expiresInHours?: number }) =>
      apiSingle<ApproveResult>(
        `/documents/${input.row.id}/actions/approve-and-mint-pat`,
        {
          method: 'POST',
          body:
            input.expiresInDays !== undefined
              ? { expiresInDays: input.expiresInDays }
              : input.expiresInHours !== undefined
                ? { expiresInHours: input.expiresInHours }
                : {},
        },
      ),
    onSuccess: (env, input) => {
      // env is the envelope; the actual result is env.data per apiSingle contract
      const result = (env as unknown as { data: ApproveResult }).data ?? (env as unknown as ApproveResult);
      setRevealed({ doc: input.row, result, origin: 'approve' });
      const ttlNote =
        typeof result.mintedPat.effectiveExpiresInDays === 'number'
          ? ` · expires in ${formatTtl(result.mintedPat.effectiveExpiresInDays)}` +
            (typeof result.mintedPat.expiryClampedFrom === 'number'
              ? ` (clamped from requested ${result.mintedPat.expiryClampedFrom}d per role policy)`
              : '')
          : '';
      toast.success(`Approved — PAT minted for ${result.mintedPat.name}${ttlNote}`);
      // Refresh the list so the doc moves from pending to approved.
      qc.invalidateQueries({ queryKey: ['external-clients-access-requests-admin'] });
    },
    onError: (e: Error) => {
      toast.error(e.message);
    },
  });

  // 0.1.55 CTSK-166 Phase D — deny counterpart to approve. Mirrors
  // apps/api/src/routes/documents.ts /actions/deny-access-request.
  // The denial reason comes from a browser prompt() for the MVP UI;
  // upgrade to a proper dialog with reason validation is a follow-up
  // FR after this initial pass ships.
  const denyMut = useMutation({
    mutationFn: (input: { row: ClientRow; denialReason: string }) =>
      apiSingle<{
        documentId: string;
        workflowName: string;
        deniedAt: string;
        deniedByUserId: string;
        denialReason: string;
      }>(
        `/documents/${input.row.id}/actions/deny-access-request`,
        { method: 'POST', body: { denialReason: input.denialReason } },
      ),
    onSuccess: (_env, input) => {
      toast.success(`Denied: ${input.row.title}`);
      qc.invalidateQueries({ queryKey: ['external-clients-access-requests-admin'] });
    },
    onError: (e: Error) => {
      toast.error(e.message);
    },
  });

  // 0.1.5X CTSK-170 Part A/5 — Change-role counterpart to approve.
  // Rotates an already-approved client's PAT to a new named role handle.
  // Owner-only server-side (pats:change_client_role cap); non-owner
  // operators see the button but the POST returns 403 which surfaces
  // via toast. MVP UI uses prompt() for role handle (matches the deny
  // action's window-prompt style); FR filed for a proper role-picker
  // modal with the catalog rendered as a select + rationale textarea.
  //
  // Reuses the `revealed` state — the one-time raw token from the new
  // PAT shows up in the same reveal panel as approve mints. The
  // ChangeRoleResult shape has the same `mintedPat` sub-object so the
  // panel renders without branching.
  const roleCatalogQ = useQuery({
    queryKey: ['role-catalog'],
    queryFn: () => apiSingle<{ roles: RoleCatalogEntry[] }>('/role-catalog'),
    // Lazy — only fetch when the operator opens the Approved tab, and
    // the response is cached for the session. Catalog is static
    // server-side so no stale-refetch policy needed.
    staleTime: Infinity,
    enabled: activeState === 'approved' || activeState === 'all',
  });

  const changeRoleMut = useMutation({
    mutationFn: (input: {
      row: ClientRow;
      newRole: string;
      rationale?: string;
    }) =>
      apiSingle<ChangeRoleResult>(
        `/documents/${input.row.id}/actions/change-client-role`,
        {
          method: 'POST',
          body: input.rationale
            ? { newRole: input.newRole, rationale: input.rationale }
            : { newRole: input.newRole },
        },
      ),
    onSuccess: (env, input) => {
      const result = (env as unknown as { data: ChangeRoleResult }).data ?? (env as unknown as ChangeRoleResult);
      // CTSK-197 (2026-07-23): reveal panel now branches on `origin` so
      // the header + secondary line explain that this is a role-change
      // mint (new PAT replacing old) rather than a fresh approve. The
      // mintedPat sub-shape matches ApproveResult; only the panel copy
      // differs. Casting `result as ApproveResult` is safe because the
      // panel reads only `mintedPat.*` (which both types share).
      setRevealed({
        doc: input.row,
        result: result as unknown as ApproveResult,
        origin: 'role-change',
        roleChange: {
          newRoleHandle: result.newRoleHandle,
          mode: result.mode,
        },
      });
      toast.success(`Role changed → ${result.newRoleHandle} (${result.mode})`);
      qc.invalidateQueries({ queryKey: ['external-clients-access-requests-admin'] });
    },
    onError: (e: Error) => {
      toast.error(e.message);
    },
  });

  // 0.1.57 CTSK-181 — Revoke counterpart to approve / change-role. Terminal:
  // sets revoked_at on the linked PAT + swaps access/approved → access/revoked.
  // No PAT is minted, no reveal panel — the row just moves to the Revoked tab.
  // Owner-only server-side (pats:revoke_client_pat cap); non-owner operators
  // see the button but the POST returns 403 which surfaces via toast.
  // MVP UI uses window.confirm for the danger prompt + window.prompt for the
  // reason (matches the deny/change-role patterns); FR filed for a proper
  // Revoke modal with reason validation + confirm-typing gate.
  const revokeMut = useMutation({
    mutationFn: (input: { row: ClientRow; revocationReason: string }) =>
      apiSingle<{
        documentId: string;
        revokedPatId: string;
        revokedAt: string;
        revokedByUserId: string;
        revocationReason: string;
      }>(
        `/documents/${input.row.id}/actions/revoke-client-pat`,
        { method: 'POST', body: { revocationReason: input.revocationReason } },
      ),
    onSuccess: (_env, input) => {
      toast.success(`Revoked: ${input.row.title}`);
      qc.invalidateQueries({ queryKey: ['external-clients-access-requests-admin'] });
    },
    onError: (e: Error) => {
      toast.error(e.message);
    },
  });

  // Resolve the container id once. We query storage-containers and match
  // by name because the seed bundle name is the stable contract; the
  // ULID is org-specific and unknown until install-time.
  const containersQ = useQuery({
    queryKey: ['storage-containers-for-external-clients'],
    queryFn: () => apiList<{ id: string; name: string; slug: string }>('/storage-containers'),
  });
  const containerId = containersQ.data?.data.find((c) => c.name === CONTAINER_NAME)?.id;

  // CTSK-193 (2026-07-22): sources from GET /nests/:slug/access-requests
  // (the CTSK-166 Phase C endpoint) instead of the general /documents
  // list. Reasons:
  //   1. Server-side filter to access-request docs — no more client-side
  //      tag scan + no more polluted-container problem the earlier
  //      hotfix worked around.
  //   2. AccessRequestSummary shape includes server-enriched
  //      _by_display_name fields from CTSK-186's users-table join —
  //      previously the UI could only show ULID prefixes.
  //   3. Stable schema — the AccessRequestSummary trimmed shape is
  //      versioned; /documents jsonb is not.
  const documentsQ = useQuery({
    // CTSK-194 (2026-07-22): queryKey + URL renamed. Pre-CTSK-193 this
    // was /documents?containerId=X (queryKey ['external-clients-docs']).
    // CTSK-193 changed the URL to the self-scoped
    // /nests/:slug/access-requests but that endpoint is guarded by
    // `documents:view_own_access_request` + requires a source_tag
    // discriminator (either ?source_tag=X or caller-PAT sourceTag) —
    // cookie-auth operators have neither, so every load 400'd and
    // rendered an empty list. The new /nests/:slug/access-requests/admin
    // endpoint is org-scoped, guarded by `pats:view_all` (held by
    // cookie owner + operator + `pats-operator` role), and returns
    // the same AccessRequestSummary[] shape — no consumer change.
    // Also note: CTSK-193 forgot to update the 4 mutation
    // invalidateQueries() calls to match the renamed queryKey, so
    // mutations silently no-op'd on cache refresh. Fixed same-file.
    queryKey: ['external-clients-access-requests-admin'],
    queryFn: () =>
      apiList<AccessRequestSummary>('/nests/external-clients/access-requests/admin'),
    // No containerId gating needed — the endpoint is nest-slug-scoped.
    enabled: true,
  });

  const rows = useMemo<ClientRow[]>(() => {
    if (!documentsQ.data) return [];
    return documentsQ.data.data
      .map((r) => {
        // CTSK-193: AccessRequestSummary shape — `tags` is already
        // `string[]`, `customProperties` is the trimmed CP set with
        // server-enriched _by_display_name fields inline.
        const cp = r.customProperties ?? {};
        return {
          id: r.id,
          title: r.title,
          createdAt: r.createdAt,
          updatedAt: r.updatedAt,
          tags: r.tags,
          accessState: deriveAccessState(r.tags),
          clientKind: deriveClientKind(r.tags),
          approvedAt: cp.approved_at ?? null,
          approvedByUserId: cp.approved_by_user_id ?? null,
          approvedByDisplayName: cp.approved_by_display_name ?? null,
          deniedAt: null, // CTSK-193: not in the trimmed shape (yet); add to route if UI needs it
          deniedByUserId: cp.denied_by_user_id ?? null,
          deniedByDisplayName: cp.denied_by_display_name ?? null,
          denialReason: cp.denial_reason ?? null,
          revokedAt: null, // Same as deniedAt above — trimmed shape doesn't include revoked_at timestamp
          revokedByUserId: cp.revoked_by_user_id ?? null,
          revokedByDisplayName: cp.revoked_by_display_name ?? null,
          revocationReason: cp.revocation_reason ?? null,
          // CTSK-296: TTL trio from the trimmed CP shape (server emits
          // numbers or nulls; pre-CTSK-296 servers omit → null).
          requestedExpiresInDays: cp.requested_expires_in_days ?? null,
          effectiveExpiresInDays: cp.effective_expires_in_days ?? null,
          expiryClampedFrom: cp.expiry_clamped_from ?? null,
          // CTSK-300: introduction rows get their own tab + row shape.
          isIntroduction: r.tags.includes(INTRODUCTION_TAG),
          clientKindRaw: cp.client_kind ?? null,
          patLifecycle: r.patLifecycle ?? null,
        };
      })
      // 0.1.55 hotfix — the "External Clients" storage container can
      // hold docs that AREN'T access requests (e.g. Vault got the
      // container set as its default-intake target somewhere along
      // the way, dumping every raw ingest into it — 397 unrelated
      // docs polluted the landing page). Filter to docs that carry
      // at least one `access/*` discriminator tag so only real
      // access requests appear.
      //
      // FR captured 2026-07-10 by chadwiki to eventually push this
      // filter into the API query (either via a scoped ?tag=access/*
      // predicate or a new /nests/:slug/access-requests endpoint
      // consumption for consistent server-side scoping). This
      // client-side filter is the fast fix for the data-leak
      // symptom.
      .filter((r) => r.tags.some((t) => t.startsWith('access/')));
  }, [documentsQ.data]);

  // Counts across states — populates the tab strip badges even when
  // `activeState` is filtering to something else.
  const counts = useMemo(() => {
    const c: Record<Exclude<AccessState, 'all'>, number> = {
      pending: 0, approved: 0, denied: 0, revoked: 0,
    };
    for (const r of rows) {
      if (r.accessState !== 'unknown') c[r.accessState] += 1;
    }
    return c;
  }, [rows]);

  // CTSK-300 — introduction rows, for the Introductions tab + its
  // tab-strip count. State tabs exclude them naturally (their derived
  // accessState is 'unknown'); "All" keeps the pre-existing mixed view.
  const introRows = useMemo(() => rows.filter((r) => r.isIntroduction), [rows]);

  const filteredRows = useMemo(() => {
    if (activeState === 'all') return rows;
    if (activeState === 'introductions') return introRows;
    return rows.filter((r) => r.accessState === activeState);
  }, [rows, introRows, activeState]);

  // CTSK-466 — All-tab pipeline: tab → archive → search → kind → state
  // → sort (filters before sort so counts stay honest; toolbar filters
  // exist only on the All tab, other tabs pass through untouched).
  const isAllTab = activeState === 'all';
  const archivedCount = useMemo(
    () => (isAllTab ? filteredRows.filter((r) => isArchivedClientRow(r)).length : 0),
    [isAllTab, filteredRows],
  );
  // CTSK-476 — Approved tab: hide dead-credential rows unless the
  // operator flips to "All-time-approved". Other tabs pass through.
  const isApprovedTab = activeState === 'approved';
  const deadCredentialCount = useMemo(
    () => (isApprovedTab ? filteredRows.filter((r) => isDeadCredential(r.patLifecycle)).length : 0),
    [isApprovedTab, filteredRows],
  );
  // CTSK-476 — `never_minted` on an approved row means the doc claims a
  // credential the PAT table can't find (linked_pat_id missing, or
  // pointing at a deleted row). Not expected; surface once per row so
  // an operator with the console open can chase the inconsistency.
  useEffect(() => {
    for (const r of rows) {
      if (r.accessState === 'approved' && r.patLifecycle?.state === 'never_minted') {
        console.warn(
          `[CTSK-476] access-request ${r.id} is access/approved but has no resolvable linked PAT (never_minted) — data inconsistency`,
        );
      }
    }
  }, [rows]);
  const visibleRows = useMemo(() => {
    if (isApprovedTab) {
      return approvedFilter === 'active'
        ? filteredRows.filter((r) => !isDeadCredential(r.patLifecycle))
        : filteredRows;
    }
    if (!isAllTab) return filteredRows;
    let out = filteredRows;
    if (!showArchived) out = out.filter((r) => !isArchivedClientRow(r));
    const q = search.trim().toLowerCase();
    if (q) {
      out = out.filter((r) =>
        [r.title, r.clientKind?.label ?? '', r.clientKindRaw ?? '', r.accessState, r.id]
          .join('\n')
          .toLowerCase()
          .includes(q),
      );
    }
    if (kindFilter) out = out.filter((r) => r.clientKind?.tag === kindFilter);
    if (stateFilter) out = out.filter((r) => r.accessState === stateFilter);
    return out;
  }, [isAllTab, isApprovedTab, approvedFilter, filteredRows, showArchived, search, kindFilter, stateFilter]);

  // Sort applies on every access-request tab; ?sort=&dir= persisted
  // (CTSK-466 AC 1). The Introductions tab keeps its own
  // Introduction-shaped table and renders unsorted, as before.
  const { sorted: sortedRows, sort, onSortChange } = useUrlTableSort(
    visibleRows,
    ECN_SORT_ACCESSORS,
  );

  // ── Render ────────────────────────────────────────────────────────

  if (containersQ.isLoading || (containerId && documentsQ.isLoading)) {
    return <SkeletonRows rows={8} />;
  }

  if (!containerId) {
    return (
      <div style={{ padding: 24 }}>
        <div className="page-header" style={{ marginBottom: 12 }}>
          <div>
            <h1 className="page-title" style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
              <KeyRound size={22} aria-hidden="true" />
              External Clients
            </h1>
          </div>
        </div>
        <div style={{
          background: 'var(--surface-raised)',
          padding: 16,
          borderRadius: 'var(--radius-md)',
          border: '1px solid var(--border-subtle)',
          color: 'var(--text-secondary)',
        }}>
          The External Clients Nest bundle hasn't been installed in this
          organization yet. Install{' '}
          <code>seed/bundles/external-clients-nest.bundle.json</code> via
          Settings → Bundles, or run{' '}
          <code>POST /api/v1/admin/bundle/import</code> with the bundle
          payload. See{' '}
          <a
            href="/help/external-clients"
            onClick={(e) => { e.preventDefault(); onNavigate('/help/external-clients'); }}
          >
            docs/proposals/external-clients-bootstrap.md
          </a>.
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      {/* One-time token reveal — pattern mirrors PatsPanel's mint reveal
          from 0.1.40 so the operator gets a consistent shape wherever a
          raw PAT surfaces. */}
      {revealed && (
        <section
          role="alert"
          style={{
            background: 'color-mix(in srgb, var(--warning-primary) 12%, var(--surface-raised))',
            border: '1px solid var(--warning-primary)',
            borderRadius: 'var(--radius-md)',
            padding: 16,
            marginBottom: 16,
            display: 'flex', flexDirection: 'column', gap: 10,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
            <strong style={{ fontSize: 14, color: 'var(--warning-primary)' }}>
              <Check size={14} style={{ verticalAlign: '-2px', marginRight: 6 }} />
              {/* CTSK-197 (2026-07-23): header + secondary line branch on
                  origin. Role-change mints are distinct events from
                  fresh approvals — the old PAT was revoked atomically,
                  and if the operator dismisses without copying they've
                  orphaned a PAT with no straightforward recovery. The
                  header now says so explicitly. */}
              {revealed.origin === 'role-change'
                ? `Role changed → ${revealed.roleChange?.newRoleHandle ?? '?'} · new PAT minted — copy now (old PAT was revoked)`
                : 'Approved · PAT minted — copy the token now'}
            </strong>
            <button
              type="button"
              onClick={() => setRevealed(null)}
              style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 12 }}
            >
              I've saved this
            </button>
          </div>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
            <span style={{ fontWeight: 500 }}>{revealed.result.mintedPat.name}</span>
            {' · '}
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-muted)' }}>
              {revealed.result.mintedPat.prefix}…
            </span>
            {revealed.origin === 'role-change' && revealed.roleChange && (
              <>
                {' · '}
                <span style={{ color: 'var(--text-muted)' }}>
                  mode: <strong>{revealed.roleChange.mode}</strong>
                </span>
              </>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <code
              style={{
                flex: 1,
                fontFamily: 'var(--font-mono)', fontSize: 13,
                padding: '8px 12px',
                background: 'var(--surface-raised)',
                border: '1px solid var(--border-strong)',
                borderRadius: 4,
                userSelect: 'all',
                wordBreak: 'break-all',
              }}
            >
              {revealed.result.mintedPat.token}
            </code>
            <button
              type="button"
              onClick={() => {
                // CTSK-172: use copyToClipboard helper. The prior
                // navigator.clipboard.writeText call fails silently on
                // http:// origins (Vault UI runs on
                // http://192.168.153.164:5173/ — not a secure context).
                void copyToClipboard(revealed.result.mintedPat.token).then((ok) => {
                  if (ok) toast.success('Token copied');
                  else toast.error('Clipboard write failed');
                });
              }}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                padding: '8px 12px', fontSize: 12,
                border: '1px solid var(--border-strong)', borderRadius: 4,
                background: 'var(--surface-raised)', cursor: 'pointer',
              }}
            >
              <Copy size={12} /> Copy
            </button>
            {/* CTSK-176: Download-as-.env button. Disabled when sourceTag
                is missing. CTSK-197 (2026-07-23) confirmed both approve
                AND change-role adapters now emit sourceTag + subUuidTail
                (the "fast-follow" is shipped — change-role inherits both
                from oldPat in change-client-role.ts). Button is
                effectively always enabled for the current adapters;
                stays defensive against future mint paths that might
                lack sourceTag. */}
            <button
              type="button"
              disabled={!revealed.result.mintedPat.sourceTag}
              onClick={() => {
                const sourceTag = revealed.result.mintedPat.sourceTag;
                if (!sourceTag) return;
                // CTSK-211: the editable field is the single source of
                // truth at download time. It was pre-filled from
                // pickApiBaseUrl()'s chain (VITE build-time env →
                // gated server apiBaseUrl (CTSK-187) → page-origin
                // port swap) and possibly hand-edited by the operator.
                // Blank field falls back to re-running the chain.
                const fieldValue = apiBaseUrlField.trim();
                const baseUrl = fieldValue
                  ? fieldValue.replace(/\/+$/, '')
                  : pickApiBaseUrl(revealed.result.mintedPat.apiBaseUrl);
                // CTSK-311: role-aware emission — tootasko-ext mints get
                // the 4 extra TASKO_* keys. Role-change reveals prefer the
                // NEW handle from the rotation (server emits it on both
                // paths; the client-side roleChange fallback covers rolling
                // deploys where mintedPat.roleHandle is absent).
                const content = buildSessionEnv(revealed.result.mintedPat, baseUrl, sourceTag, {
                  roleHandle:
                    revealed.result.mintedPat.roleHandle ??
                    revealed.roleChange?.newRoleHandle ??
                    null,
                  activeOrgId: revealed.result.mintedPat.activeOrgId ?? null,
                  webUiUrl: window.location.origin,
                });
                const filename = envFilenameFor(sourceTag);
                triggerDownload(content, filename);
                toast.success(`Downloaded ${filename}`);
              }}
              title={
                revealed.result.mintedPat.sourceTag
                  ? `Download as ~/.config/tasko-mcp/${envFilenameFor(revealed.result.mintedPat.sourceTag)}`
                  : 'sourceTag unavailable for this mint — download disabled'
              }
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                padding: '8px 12px', fontSize: 12,
                border: '1px solid var(--border-strong)', borderRadius: 4,
                background: revealed.result.mintedPat.sourceTag
                  ? 'var(--surface-raised)' : 'var(--surface-muted)',
                cursor: revealed.result.mintedPat.sourceTag ? 'pointer' : 'not-allowed',
                opacity: revealed.result.mintedPat.sourceTag ? 1 : 0.5,
              }}
            >
              <Download size={12} /> Download .env
            </button>
          </div>
          {/* CTSK-211: always-visible editable API base URL. Pre-filled
              with pickApiBaseUrl()'s chain winner; the operator edits it
              here when every automatic slot guessed wrong (AC#3). Q4
              ratification settled always-visible over an "advanced"
              disclosure — a wrong default the operator never sees is a
              wrong .env. */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label
              htmlFor="ctsk211-api-base-url"
              style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)' }}
            >
              API base URL <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>
                (stamped into the .env as <code style={{ fontSize: 11 }}>TASKO_BASE_URL</code>)
              </span>
            </label>
            <input
              id="ctsk211-api-base-url"
              type="text"
              value={apiBaseUrlField}
              onChange={(e) => setApiBaseUrlField(e.target.value)}
              spellCheck={false}
              style={{
                fontFamily: 'var(--font-mono)', fontSize: 12,
                padding: '6px 10px',
                background: 'var(--surface-raised)',
                border: '1px solid var(--border-strong)',
                borderRadius: 4,
                color: 'var(--text-primary)',
              }}
            />
            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
              derived from page origin
            </span>
          </div>
          {/* CTSK-176: save-location note. Shows the target path + chmod
              step so the operator knows what to do with the downloaded
              file. Only rendered when sourceTag is available. */}
          {revealed.result.mintedPat.sourceTag && (
            <div style={{ fontSize: 11, color: 'var(--text-muted)', display: 'flex', flexDirection: 'column', gap: 2 }}>
              <span>
                Save to: <code style={{ fontSize: 11 }}>~/.config/tasko-mcp/{envFilenameFor(revealed.result.mintedPat.sourceTag)}</code>
              </span>
              <span>
                Then: <code style={{ fontSize: 11 }}>chmod 600 ~/.config/tasko-mcp/{envFilenameFor(revealed.result.mintedPat.sourceTag)}</code>
              </span>
            </div>
          )}
          <p style={{ margin: 0, fontSize: 11, color: 'var(--text-muted)' }}>
            This is the only time the raw token will be shown. Hand it to{' '}
            <strong>{revealed.doc.title}</strong> immediately and clear it from anywhere
            you paste it. Granted capabilities:{' '}
            <code style={{ fontSize: 11 }}>
              {revealed.result.mintedPat.grantedCapabilities.join(', ')}
            </code>
          </p>
          {revealed.result.mintedPat.droppedCapabilities.length > 0 && (
            <div style={{ fontSize: 12, color: 'var(--warning-primary)' }}>
              <strong>Note:</strong> the following requested capabilities were dropped
              (you don't currently hold them):{' '}
              <code>{revealed.result.mintedPat.droppedCapabilities.join(', ')}</code>
            </div>
          )}
        </section>
      )}

      <div className="page-header" style={{ marginBottom: 12 }}>
        <div>
          <h1 className="page-title" style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
            <KeyRound size={22} aria-hidden="true" />
            External Clients
          </h1>
          <div className="page-subtitle">
            Access records for external agents/clients that read or write
            Captain Tasko via API. See{' '}
            <a
              href="#"
              onClick={(e) => { e.preventDefault(); onNavigate('/workitems/CTSK-166'); }}
            >
              CTSK-166
            </a>{' '}
            for the design.
          </div>
        </div>
        {/* CTSK-383 — nest-detail help link now targets the operator
            walkthrough (approve/deny/re-role/revoke/audit flows); the
            walkthrough links onward to nests-auth-matrix for the
            who-can-do-what matrix (CTSK-355 original target). */}
        <HelpLink
          pageName="External Clients"
          slug="external-clients-nest"
          onNavigate={onNavigate}
        />
      </div>

      {/* Onboarding card — CTSK-TBD (child of CTSK-166) redesign of the
          0.1.55+ info card: header row with the copyable Request note
          chip, a 2-sentence lead, four numbered operator steps, the
          session-UID discovery strip, the API reference row, and the
          admin-view footnote. Every link routes in-app via onNavigate. */}
      <section
        data-testid="ecn-onboarding-card"
        style={{
          marginBottom: 16,
          padding: 14,
          background: 'color-mix(in srgb, var(--accent-primary) 6%, var(--surface-raised))',
          border: '1px solid color-mix(in srgb, var(--accent-primary) 30%, var(--border-subtle))',
          borderRadius: 'var(--radius-md)',
          fontSize: 13,
          lineHeight: 1.55,
        }}
      >
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          <KeyRound size={18} style={{ marginTop: 2, color: 'var(--accent-primary)', flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            {/* 1 · Header row — title left, chip right; wraps below ~600px. */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 8,
                flexWrap: 'wrap',
                marginBottom: 6,
              }}
            >
              <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                Onboarding a new client
              </div>
              <RequestNoteChip prompt={REQUEST_NOTE_PROMPT} onNavigate={onNavigate} />
            </div>

            {/* 2 · Lead paragraph */}
            <p style={{ margin: 0, color: 'var(--text-secondary)' }}>
              A client submits an access request from its own tasko-mcp session (paste the Request note
              above into a fresh session, or use a direct <code>POST /api/v1/documents/ingest</code>) and it
              lands here as a <strong style={{ color: ACCESS_COLORS.pending }}>pending</strong> row.
              Approving mints the client's PAT and shows the raw token exactly{' '}
              <strong style={{ color: 'var(--danger-primary)' }}>once</strong> — hand it off out-of-band.
            </p>

            {/* 3 · Numbered steps */}
            <ol
              data-testid="ecn-onboarding-steps"
              style={{ margin: '10px 0 0', paddingLeft: 22, color: 'var(--text-secondary)' }}
            >
              <li>
                <strong style={{ color: 'var(--text-primary)' }}>Verify</strong> the pending row before you
                touch it:
                <ul style={{ margin: '2px 0 0', paddingLeft: 18 }}>
                  <li>the friendly name is one you recognise,</li>
                  <li>
                    <code>source_tag</code> matches a known client,
                  </li>
                  <li>the requested capabilities stay least-privilege for the stated rationale.</li>
                </ul>
              </li>
              <li style={{ marginTop: 4 }}>
                <strong style={{ color: 'var(--text-primary)' }}>Approve &amp; mint PAT</strong> — the token
                is intersected against your own capabilities and shown exactly once.
              </li>
              <li style={{ marginTop: 4 }}>
                <strong style={{ color: 'var(--text-primary)' }}>Hand off</strong> the token through the
                secure channel you already agreed on, or <em>Download as .env</em> to save it straight into{' '}
                <code>~/.config/tasko-mcp/</code>.
              </li>
              <li style={{ marginTop: 4 }}>
                <strong style={{ color: 'var(--text-primary)' }}>Manage</strong> approved rows with{' '}
                <em>Change role</em> (rotates the PAT) and <em>Revoke</em> (terminates it); every transition
                writes an audit-trail row on the request doc stamped with the API version at that moment.
              </li>
            </ol>

            {/* 4 · "Finding the session UID & tail" strip */}
            <div
              data-testid="ecn-uid-discovery"
              style={{
                marginTop: 12,
                padding: '10px 12px',
                background: 'var(--surface-raised)',
                border: '1px solid var(--border-subtle)',
                borderRadius: 'var(--radius-md)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600, color: 'var(--text-primary)', fontSize: 12 }}>
                <Fingerprint size={14} aria-hidden style={{ color: 'var(--accent-primary)' }} />
                Finding the session UID &amp; tail
              </div>
              <div style={{ marginTop: 6, display: 'flex', gap: 12, flexWrap: 'wrap', fontSize: 12 }}>
                {UID_DISCOVERY_LINKS.map((l, i) => (
                  <span key={l.path} style={{ display: 'inline-flex', alignItems: 'center', gap: 12 }}>
                    {i > 0 && <span aria-hidden style={{ color: 'var(--text-muted)' }}>·</span>}
                    <a
                      href={l.path}
                      onClick={(e) => { e.preventDefault(); onNavigate(l.path); }}
                      style={{ color: 'var(--accent-primary)', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                    >
                      {l.label}
                      <ExternalLink size={13} aria-hidden />
                    </a>
                  </span>
                ))}
              </div>
              <div style={{ marginTop: 6, fontSize: 12, color: 'var(--text-secondary)' }}>
                <code>local_&lt;hex-uuid&gt;</code> is a desktop session (lowercased);{' '}
                <code>cse_&lt;24-char&gt;</code> is a cloud session (case-sensitive). The 8-char tail is the
                first eight characters after the prefix (hyphens stripped) — read{' '}
                <code>sessionMetadata.subUuid</code> from the env block <code>tasko_load_session_env</code>{' '}
                returns at boot.
              </div>
            </div>

            {/* 5 · API reference links row */}
            <div
              data-testid="ecn-api-refs"
              style={{ marginTop: 10, display: 'flex', gap: 12, flexWrap: 'wrap', fontSize: 12 }}
            >
              {API_REFERENCE_LINKS.map((l, i) => (
                <span key={l.path} style={{ display: 'inline-flex', alignItems: 'center', gap: 12 }}>
                  {i > 0 && <span aria-hidden style={{ color: 'var(--text-muted)' }}>·</span>}
                  <a
                    href={l.path}
                    onClick={(e) => { e.preventDefault(); onNavigate(l.path); }}
                    style={{ color: 'var(--accent-primary)', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                  >
                    {l.label}
                    <ExternalLink size={13} aria-hidden />
                  </a>
                </span>
              ))}
            </div>

            {/* 6 · "About this admin view" footnote */}
            <div
              data-testid="ecn-admin-view-note"
              style={{ marginTop: 10, fontSize: 12, color: 'var(--text-muted)' }}
            >
              <strong style={{ fontWeight: 600 }}>About this admin view.</strong> Bootstrap PAT scope:{' '}
              <code>documents:create</code> + <code>documents:view_own_access_request</code>. Client-scoped
              by <code>source_tag</code>; can't see other tenants' requests. This admin table (visible to you)
              is served by <code>GET /nests/external-clients/access-requests/admin</code> which is org-scoped
              and guarded by the <code>pats:view_all</code> capability introduced in CTSK-194 — held
              automatically by cookie owner/operator sessions, or via the <code>pats-operator</code> role for
              programmatic dashboards.
            </div>
          </div>
        </div>
      </section>

      {/* Tab strip — CTSK-300: converted from ad-hoc btn--ghost styling
          to the shared .tabs/.tab classes, scoped under
          `tabs--external-clients` (same cascade-containment lesson as
          CTSK-298's `tabs--settings` — the base classes are shared with
          Watching, FileTasks and DocumentDetail and must not change).
          Active tab: bold (scoped CSS) + state-colored underline
          (accent for Introductions/All). Counts render as subtle gray
          text (no pill) so the eye reads the label first. */}
      <div className="tabs tabs--external-clients">
        {TAB_KEYS.map((tab) => {
          const isActive = activeState === tab;
          const count =
            tab === 'all' ? rows.length
            : tab === 'introductions' ? introRows.length
            : counts[tab];
          const color =
            tab === 'all' || tab === 'introductions'
              ? 'var(--accent-primary)'
              : ACCESS_COLORS[tab];
          return (
            <button
              key={tab}
              type="button"
              onClick={() => {
                setActiveState(tab);
                // CTSK-357 — reflect the selection in the URL so tabs
                // are shareable deep-links and Back restores the strip.
                // Same-pathname pushState keeps the page mounted; the
                // tabParam effect re-sync is then a no-op.
                onNavigate(`/mission-control/nests/external-clients?tab=${tab}`);
              }}
              className={isActive ? 'tab active' : 'tab'}
              style={isActive ? { color, borderBottomColor: color } : undefined}
            >
              {tab}
              <span className="tab-count">{count}</span>
            </button>
          );
        })}
      </div>

      {/* CTSK-466 — All-tab toolbar: search + Kind + State dropdowns +
          archive toggle, right-aligned above the table (distinct from
          the tab strip on the left). */}
      {isAllTab && (
        <ToolbarRow
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search client, kind, id…"
          kindOptions={ECN_KIND_OPTIONS}
          kind={kindFilter}
          onKindChange={setKindFilter}
          stateOptions={ECN_STATE_OPTIONS}
          state={stateFilter}
          onStateChange={setStateFilter}
          showArchived={showArchived}
          onShowArchivedChange={setShowArchived}
          archivedHiddenCount={archivedCount}
        />
      )}

      {/* CTSK-466 — count-under-filter (CTSK-461 discipline): say when
          the toolbar/archive filters narrow the All-tab view. */}
      {isAllTab && sortedRows.length !== rows.length && (
        <p style={{ margin: '0 0 8px 0', fontSize: 12, color: 'var(--text-muted)' }}>
          Showing {sortedRows.length} of {rows.length}
          {!showArchived && archivedCount > 0 && <> ({archivedCount} archived hidden)</>}
        </p>
      )}

      {/* CTSK-476 — Approved-tab segmented control: credential-state
          filter. Sits where the All-tab toolbar would (above the table,
          left-aligned with the strip). */}
      {isApprovedTab && filteredRows.length > 0 && (
        <div
          role="radiogroup"
          aria-label="Approved credential filter"
          data-testid="approved-credential-filter"
          style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '0 0 8px 0', flexWrap: 'wrap' }}
        >
          <div style={{ display: 'inline-flex', border: '1px solid var(--border-color)', borderRadius: 6, overflow: 'hidden' }}>
            {APPROVED_FILTER_OPTIONS.map((opt) => {
              const active = approvedFilter === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  onClick={() => setApprovedFilter(opt.value)}
                  style={{
                    padding: '4px 10px',
                    fontSize: 12,
                    fontWeight: active ? 600 : 400,
                    border: 'none',
                    cursor: 'pointer',
                    background: active ? 'var(--accent-primary)' : 'transparent',
                    color: active ? '#fff' : 'var(--text-muted)',
                  }}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            {approvedFilter === 'active' && deadCredentialCount > 0
              ? `Showing ${sortedRows.length} of ${filteredRows.length} (${deadCredentialCount} revoked/expired hidden)`
              : deadCredentialCount > 0
                ? `${deadCredentialCount} of ${filteredRows.length} approved rows have a revoked or expired credential`
                : 'All approved credentials are live'}
          </span>
        </div>
      )}

      {/* Table */}
      {isApprovedTab && sortedRows.length === 0 && filteredRows.length > 0 ? (
        // CTSK-476 — every approved row's credential is dead and the
        // filter is hiding them all.
        <div style={{ padding: 24, color: 'var(--text-muted)', textAlign: 'center' }}>
          No approved clients with a live credential. Switch to <strong>All-time-approved</strong> to see
          the {deadCredentialCount} revoked/expired {deadCredentialCount === 1 ? 'row' : 'rows'}.
        </div>
      ) : isAllTab && sortedRows.length === 0 && filteredRows.length > 0 ? (
        // CTSK-466 — rows exist but the toolbar/archive filters hid
        // them all.
        <div style={{ padding: 24, color: 'var(--text-muted)', textAlign: 'center' }}>
          No clients match the current filters.
        </div>
      ) : filteredRows.length === 0 ? (
        <div style={{ padding: 24, color: 'var(--text-muted)', textAlign: 'center' }}>
          {activeState === 'introductions' ? (
            <>No <strong>introductions</strong> yet.</>
          ) : (
            <>No <strong>{activeState}</strong> clients {activeState === 'all' ? 'in this org yet' : 'yet'}.</>
          )}
          {activeState === 'introductions' && (
            <div style={{ marginTop: 8, fontSize: 13 }}>
              Sessions appear here when they announce themselves via{' '}
              <code>tasko_greet</code> — identity-only, before any capability request.
            </div>
          )}
          {activeState === 'pending' && (
            <div style={{ marginTop: 8, fontSize: 13 }}>
              New requests arrive via <code>tasko_request_access</code> or a direct
              <code style={{ marginLeft: 4 }}>POST /api/v1/documents/ingest</code>. See the
              onboarding card above.
            </div>
          )}
          {activeState === 'approved' && (
            <div style={{ marginTop: 8, fontSize: 13 }}>
              Approving a pending request via the row action here promotes it into this tab
              and mints the client's real PAT.
            </div>
          )}
        </div>
      ) : activeState === 'introductions' ? (
        /* CTSK-300 — Introduction rows get an Introduction-shaped table:
           Client (friendly name == sourceTag) / Introduced (relative
           created_at) / Kind (client_kind CP). No State column
           (introductions never transition through the request
           workflow), no Actions (informational docs — no
           revoke/deny concept), and no `—` fallbacks. */
        // CTSK-466 slice 6 — style port: the intro table adopts the
        // shared .grid treatment (PatsPanel's density) alongside the
        // access-request table; .table--external-clients now carries
        // only the column width caps.
        <table className="grid table--external-clients">
          <thead>
            <tr>
              <th>Client</th>
              <th>Introduced</th>
              <th className="col--kind">Kind</th>
            </tr>
          </thead>
          <tbody>
            {filteredRows.map((r) => (
              <tr
                key={r.id}
                onClick={() => onNavigate(`/documents/${r.id}`)}
                style={{ cursor: 'pointer' }}
              >
                <td>
                  <span style={{ fontWeight: 500 }}>{introFriendlyName(r.title)}</span>
                </td>
                <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                  {formatRelative(r.createdAt)}
                </td>
                <td className="col--kind">
                  {r.clientKindRaw && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
                      <Bot size={12} aria-hidden="true" />
                      {INTRO_KIND_LABELS[r.clientKindRaw] ?? r.clientKindRaw}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        // CTSK-466 — header renders via the shared RowTable shell
        // (SortableTh per column, ?sort=&dir= persisted) and the table
        // adopts the .grid treatment (slice 6 style port; PatsPanel is
        // the reference style per the ticket).
        <RowTable
          columns={ECN_COLUMNS}
          sort={sort}
          onSortChange={onSortChange}
          className="grid table--external-clients"
        >
            {sortedRows.map((r) => {
              // CTSK-476 — approved rows whose credential is dead render
              // greyed with a REVOKED/EXPIRED badge next to the state chip.
              const deadCredential = r.accessState === 'approved' && isDeadCredential(r.patLifecycle);
              return (
              <tr
                key={r.id}
                onClick={() => onNavigate(`/documents/${r.id}`)}
                style={{ cursor: 'pointer', opacity: deadCredential ? 0.55 : undefined }}
                data-credential-state={r.accessState === 'approved' ? (r.patLifecycle?.state ?? 'unknown') : undefined}
              >
                <td>
                  <span style={{ fontWeight: 500 }}>{r.title}</span>
                </td>
                <td className="col--kind">
                  {r.clientKind ? (
                    // CTSK-466 — Kind chip-click filter (All tab only —
                    // the toolbar the filter lives in is only rendered
                    // there; chips stay inert on single-state tabs).
                    isAllTab ? (
                      <ChipFilterButton
                        active={kindFilter === r.clientKind.tag}
                        title={
                          kindFilter === r.clientKind.tag
                            ? 'Clear kind filter'
                            : `Filter by kind: ${r.clientKind.label}`
                        }
                        onToggle={() =>
                          setKindFilter(kindFilter === r.clientKind!.tag ? '' : r.clientKind!.tag)
                        }
                      >
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
                          <r.clientKind.icon size={12} aria-hidden="true" />
                          {r.clientKind.label}
                        </span>
                      </ChipFilterButton>
                    ) : (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
                        <r.clientKind.icon size={12} aria-hidden="true" />
                        {r.clientKind.label}
                      </span>
                    )
                  ) : (
                    <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>—</span>
                  )}
                </td>
                <td className="col--state">
                  {r.accessState === 'unknown' ? (
                    <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>—</span>
                  ) : (() => {
                    // CTSK-466 slice 6 — chip typography aligned with
                    // PatsPanel's StateBadge (adds whiteSpace: nowrap;
                    // metrics were already identical).
                    const chip = (
                      <span style={{
                        display: 'inline-block',
                        padding: '2px 8px',
                        borderRadius: 10,
                        background: ACCESS_COLORS[r.accessState as Exclude<AccessState, 'all'>] + '20',
                        color: ACCESS_COLORS[r.accessState as Exclude<AccessState, 'all'>],
                        fontSize: 11,
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: 0.4,
                        whiteSpace: 'nowrap',
                      }}>
                        {r.accessState}
                      </span>
                    );
                    // CTSK-476 — credential badge: the doc says approved,
                    // the PAT table says otherwise. Reuses PatsPanel's
                    // StateBadge (same vocabulary) + a YYYY-MM-DD suffix
                    // for revoked (revoked_at) / expired (expires_at).
                    const lc = r.patLifecycle;
                    const credentialBadge = deadCredential && lc ? (
                      <span
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginLeft: 6 }}
                        title={
                          lc.state === 'revoked'
                            ? `Linked PAT revoked ${lc.revokedAt ?? ''} — credential no longer usable`
                            : `Linked PAT expired ${lc.expiresAt ?? ''} — credential no longer usable`
                        }
                      >
                        <StateBadge state={lc.state === 'revoked' ? 'revoked' : 'expired'} />
                        {(() => {
                          const d = isoDate(lc.state === 'revoked' ? lc.revokedAt : lc.expiresAt);
                          return d ? <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>· {d}</span> : null;
                        })()}
                      </span>
                    ) : null;
                    // CTSK-466 — State chip-click filter (All tab only).
                    return (
                      <>
                        {isAllTab ? (
                          <ChipFilterButton
                            active={stateFilter === r.accessState}
                            title={
                              stateFilter === r.accessState
                                ? 'Clear state filter'
                                : `Filter by state: ${r.accessState}`
                            }
                            onToggle={() =>
                              setStateFilter(stateFilter === r.accessState ? '' : r.accessState)
                            }
                          >
                            {chip}
                          </ChipFilterButton>
                        ) : (
                          chip
                        )}
                        {credentialBadge}
                      </>
                    );
                  })()}
                </td>
                <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                  {/* CTSK-300 density polish — Requested column
                      restructured from 3 lines to 2. Line 1: primary
                      time (when this doc landed). Line 2 (small gray):
                      attribution + TTL combined as inline `·`-joined
                      chunks. Retains the CTSK-296 TTL trio (effective
                      TTL wins the slot once approved; requester
                      preference shows pre-approval; clamp marker kept)
                      and the CTSK-186/193 display-name attribution with
                      ULID-prefix fallback. */}
                  <div>{formatRelative(r.createdAt)}</div>
                  {(() => {
                    const chunks: ReactNode[] = [];
                    if (r.accessState === 'approved' && (r.approvedByDisplayName || r.approvedByUserId)) {
                      chunks.push(
                        <span key="approved-by">
                          Approved by{' '}
                          {r.approvedByDisplayName
                            ? r.approvedByDisplayName
                            : <code style={{ fontSize: 10 }} title={r.approvedByUserId ?? ''}>{r.approvedByUserId!.slice(0, 12)}…</code>}
                        </span>,
                      );
                      if (r.approvedAt) {
                        chunks.push(<span key="approved-at">{formatRelative(r.approvedAt)}</span>);
                      }
                    }
                    if (r.accessState === 'denied' && (r.deniedByDisplayName || r.deniedByUserId)) {
                      chunks.push(
                        <span key="denied-by">
                          Denied by{' '}
                          {r.deniedByDisplayName
                            ? r.deniedByDisplayName
                            : <code style={{ fontSize: 10 }} title={r.deniedByUserId ?? ''}>{r.deniedByUserId!.slice(0, 12)}…</code>}
                        </span>,
                      );
                      if (r.denialReason) {
                        chunks.push(<span key="denied-why" style={{ fontStyle: 'italic' }}>{r.denialReason}</span>);
                      }
                    }
                    if (r.accessState === 'revoked' && (r.revokedByDisplayName || r.revokedByUserId)) {
                      chunks.push(
                        <span key="revoked-by">
                          Revoked by{' '}
                          {r.revokedByDisplayName
                            ? r.revokedByDisplayName
                            : <code style={{ fontSize: 10 }} title={r.revokedByUserId ?? ''}>{r.revokedByUserId!.slice(0, 12)}…</code>}
                        </span>,
                      );
                      if (r.revocationReason) {
                        chunks.push(<span key="revoked-why" style={{ fontStyle: 'italic' }}>{r.revocationReason}</span>);
                      }
                    }
                    // TTL slot: once a mint happened the effective value
                    // is the fact that matters; before that, show the
                    // requester's stated preference.
                    if (r.effectiveExpiresInDays !== null) {
                      chunks.push(<span key="ttl">{formatTtl(r.effectiveExpiresInDays)} TTL</span>);
                    } else if (r.requestedExpiresInDays !== null) {
                      chunks.push(<span key="ttl-req">Requested TTL: {r.requestedExpiresInDays}d</span>);
                    }
                    if (r.expiryClampedFrom !== null) {
                      chunks.push(
                        <span
                          key="ttl-clamp"
                          style={{ color: '#F59E0B' }}
                          title={`Requested ${r.expiryClampedFrom} days; clamped to ${r.effectiveExpiresInDays !== null ? formatTtl(r.effectiveExpiresInDays) : 'role max'} per role policy (mintPolicy.maxExpiresInDays)`}
                        >
                          ⚠ clamped from {r.expiryClampedFrom}d
                        </span>,
                      );
                    }
                    if (chunks.length === 0) return null;
                    return (
                      <div style={{ fontSize: 11, marginTop: 2 }}>
                        {chunks.map((c, i) => (
                          <span key={i}>
                            {i > 0 && ' · '}
                            {c}
                          </span>
                        ))}
                      </div>
                    );
                  })()}
                </td>
                <td style={{ textAlign: 'right' }}>
                  {r.accessState === 'approved' && (
                    <div style={{ display: 'inline-flex', gap: 6, justifyContent: 'flex-end' }}>
                      <button
                        type="button"
                        disabled={changeRoleMut.isPending || !roleCatalogQ.data}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (changeRoleMut.isPending) return;
                          const catalog = roleCatalogQ.data;
                          if (!catalog) {
                            toast.error('Role catalog still loading — try again in a second.');
                            return;
                          }
                          // Build the picker prompt. Filter deprecated
                          // handles out of the default view; the user can
                          // still type one in and the server will accept
                          // it (with a `replacedBy` warning).
                          const active = catalog.roles.filter((r) => !r.deprecated);
                          const menu = active
                            .map((role, i) => `  ${i + 1}. ${role.handle} — ${role.description}`)
                            .join('\n');
                          const answer = window.prompt(
                            `Change role for "${r.title}".\n\n` +
                            `Available roles:\n${menu}\n\n` +
                            `Enter the role handle (e.g. "read-only-observer"):`,
                          );
                          if (answer === null) return;
                          const newRole = answer.trim();
                          if (!newRole) {
                            toast.error('Role handle is required.');
                            return;
                          }
                          // Validate against the catalog before firing;
                          // gives faster feedback than a server round-trip
                          // for typos. Server still authoritative.
                          if (!catalog.roles.some((r) => r.handle === newRole)) {
                            toast.error(
                              `Unknown role handle: ${newRole}. See tasko_list_roles or the picker menu for valid handles.`,
                            );
                            return;
                          }
                          const rationale = window.prompt(
                            `Rationale for changing "${r.title}" to ${newRole}?\n\n` +
                            `Optional but strongly recommended for audit — kept under 200 chars. ` +
                            `Leave blank to skip.`,
                          );
                          if (rationale === null) return; // cancel
                          const trimmed = rationale.trim();
                          if (trimmed.length > 200) {
                            toast.error('Rationale must be 200 characters or fewer.');
                            return;
                          }
                          changeRoleMut.mutate({
                            row: r,
                            newRole,
                            rationale: trimmed || undefined,
                          });
                        }}
                        /* CTSK-466 slice 6 — Actions treatment via the
                           shared shell's rowActionButtonStyle (PatsPanel
                           reference: inline-flex + gap; sizing identical;
                           same on Revoke + Deny below). */
                        style={{
                          ...rowActionButtonStyle,
                          background: 'transparent',
                          color: 'var(--accent-primary)',
                          border: '1px solid var(--accent-primary)',
                        }}
                      >
                        {changeRoleMut.isPending && changeRoleMut.variables?.row.id === r.id
                          ? 'Rotating…'
                          : 'Change role'}
                      </button>
                      {/* CTSK-181: Revoke button. Terminal — no re-issue. */}
                      <button
                        type="button"
                        disabled={revokeMut.isPending}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (revokeMut.isPending) return;
                          if (!window.confirm(
                            `Revoke access for "${r.title}"?\n\n` +
                            `TERMINAL: the client's PAT will be immediately revoked and the ` +
                            `request state moves to "revoked". A fresh access request is ` +
                            `required to re-issue. Cannot be undone.`,
                          )) return;
                          const reason = window.prompt(
                            `Revocation reason for "${r.title}"?\n\n` +
                            `Required for audit — 1-500 chars.`,
                          );
                          if (reason === null) return; // cancel
                          const trimmed = reason.trim();
                          if (!trimmed) {
                            toast.error('Revocation reason is required.');
                            return;
                          }
                          if (trimmed.length > 500) {
                            toast.error('Revocation reason must be 500 characters or fewer.');
                            return;
                          }
                          revokeMut.mutate({ row: r, revocationReason: trimmed });
                        }}
                        style={{
                          ...rowActionButtonStyle,
                          background: 'transparent',
                          color: 'var(--danger-primary, #EF4444)',
                          border: '1px solid var(--danger-primary, #EF4444)',
                        }}
                      >
                        {revokeMut.isPending && revokeMut.variables?.row.id === r.id
                          ? 'Revoking…'
                          : 'Revoke'}
                      </button>
                    </div>
                  )}
                  {r.accessState === 'pending' && (
                    <div style={{ display: 'inline-flex', gap: 6, justifyContent: 'flex-end' }}>
                      <button
                        type="button"
                        className="btn btn--primary"
                        disabled={approveMut.isPending || denyMut.isPending}
                        onClick={(e) => {
                          // stopPropagation so the row-level onClick doesn't
                          // hijack the click into the doc-detail navigation
                          e.stopPropagation();
                          if (approveMut.isPending || denyMut.isPending) return;
                          // CTSK-296 — expiry pre-fill + operator override.
                          // MVP prompt() (same style as deny/change-role;
                          // proper modal is an existing FR). Pre-fill:
                          // requester's value when stated; blank otherwise
                          // (blank → no override → server resolves
                          // requested-vs-max clamp or role default).
                          //
                          // 0.1.116 (operator feedback): bounds 1-364d
                          // (was the raw 3650 schema ceiling), defaults
                          // shown from GET /nests/:slug/mint-policy, and
                          // hours accepted via an "h" suffix ("12h").
                          const policy = mintPolicyQ.data;
                          const maxDays = policy?.overrideMaxDays ?? 364;
                          const policyLine = policy
                            ? `Role default (blank, no client preference): ${formatTtl(policy.expiresInDays ?? 0)}. ` +
                              `Client requests above ${policy.maxExpiresInDays}d get clamped.\n`
                            : '';
                          const prefill =
                            r.requestedExpiresInDays !== null ? String(r.requestedExpiresInDays) : '';
                          const answer = window.prompt(
                            `Approve access for "${r.title}"?\n\n` +
                            `This will mint a new PAT with the client's requested ` +
                            `capabilities intersected with your own. The one-time ` +
                            `token will be revealed immediately.\n\n` +
                            `PAT expiry` +
                            (r.requestedExpiresInDays !== null
                              ? ` — client requested ${r.requestedExpiresInDays}d.\n`
                              : ` — client stated no preference.\n`) +
                            policyLine +
                            `Enter days (1-${maxDays}) or hours with an "h" suffix (e.g. "12h"). ` +
                            `Leave as-is to accept; clear the field to let role policy decide:`,
                            prefill,
                          );
                          if (answer === null) return; // cancel — no mint
                          const trimmedAnswer = answer.trim();
                          let expiresInDays: number | undefined;
                          let expiresInHours: number | undefined;
                          if (trimmedAnswer !== '') {
                            if (/^\d+\s*h$/i.test(trimmedAnswer)) {
                              const h = Number(trimmedAnswer.replace(/\s*h$/i, ''));
                              if (!Number.isInteger(h) || h < 1 || h > maxDays * 24) {
                                toast.error(`Hour override must be an integer between 1 and ${maxDays * 24} (got "${trimmedAnswer}").`);
                                return;
                              }
                              expiresInHours = h;
                            } else {
                              const n = Number(trimmedAnswer);
                              if (!Number.isInteger(n) || n < 1 || n > maxDays) {
                                toast.error(`Expiry must be 1-${maxDays} days, or hours like "12h" (got "${trimmedAnswer}").`);
                                return;
                              }
                              expiresInDays = n;
                            }
                          }
                          approveMut.mutate({ row: r, expiresInDays, expiresInHours });
                        }}
                        style={{ fontSize: 12, padding: '4px 10px' }}
                      >
                        {approveMut.isPending && approveMut.variables?.row.id === r.id
                          ? 'Approving…'
                          : 'Approve & mint PAT'}
                      </button>
                      {/* 0.1.55 CTSK-166 Phase D — Deny counterpart.
                          MVP UI: prompt() for the reason. Later polish:
                          proper dialog with 500-char counter + preset
                          reasons dropdown (FR). */}
                      <button
                        type="button"
                        disabled={approveMut.isPending || denyMut.isPending}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (approveMut.isPending || denyMut.isPending) return;
                          const reason = window.prompt(
                            `Deny access for "${r.title}"?\n\n` +
                            `Enter a short reason (required; visible to no one ` +
                            `outside operators, but stored for audit trail).`
                          );
                          if (reason === null) return; // user cancelled
                          const trimmed = reason.trim();
                          if (trimmed.length === 0) {
                            toast.error('Denial reason is required — try again.');
                            return;
                          }
                          if (trimmed.length > 500) {
                            toast.error('Denial reason must be 500 characters or fewer.');
                            return;
                          }
                          denyMut.mutate({ row: r, denialReason: trimmed });
                        }}
                        style={{
                          ...rowActionButtonStyle,
                          background: 'transparent',
                          color: 'var(--danger-primary, #B3261E)',
                          border: '1px solid var(--danger-primary, #B3261E)',
                        }}
                      >
                        {denyMut.isPending && denyMut.variables?.row.id === r.id
                          ? 'Denying…'
                          : 'Deny'}
                      </button>
                    </div>
                  )}
                </td>
              </tr>
              );
            })}
        </RowTable>
      )}

      <div style={{ marginTop: 12, fontSize: 12, color: 'var(--text-muted)' }}>
        Click a row to open the client's access record. Click{' '}
        <strong>Approve &amp; mint PAT</strong> to run{' '}
        <code>approve_and_mint_pat</code> — the one-time token appears at
        the top of this page. Click <strong>Deny</strong> to reject the
        request; you'll be prompted for a short reason that's stored on
        the doc for the audit trail.
      </div>
    </div>
  );
}
